import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Wallet, TrendingDown, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Admin "cash-flow gap" widget — compares live Stripe platform balance
 * against total pending affiliate commission. Surfaces upcoming shortfalls
 * before transfers actually fail.
 *
 * Consumes GET /api/admin/affiliate/cashflow-gap. Refreshes on mount +
 * on-demand via the small refresh button.
 */
export default function AdminCashflowGapWidget() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/affiliate/cashflow-gap")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const eur = (cents) => `€${((cents || 0) / 100).toFixed(2).replace(".", ",")}`;

  const health = data?.health || "idle";
  const healthMap = {
    healthy: { Icon: CheckCircle2, bg: "border-emerald-300 bg-emerald-50", text: "text-emerald-800", label: t("admCashflowHealthHealthy") },
    idle: { Icon: CheckCircle2, bg: "border-neutral-200 bg-neutral-50", text: "text-neutral-700", label: t("admCashflowHealthIdle") },
    tight: { Icon: TrendingDown, bg: "border-amber-300 bg-amber-50", text: "text-amber-800", label: t("admCashflowHealthTight") },
    critical: { Icon: AlertTriangle, bg: "border-red-400 bg-red-50", text: "text-red-800", label: t("admCashflowHealthCritical") },
  };
  const H = healthMap[health] || healthMap.idle;

  return (
    <div className="bg-white border border-black/10 p-6 sm:p-8 mb-5" data-testid="admin-cashflow-gap-widget">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="ed-eyebrow">{t("admCashflowEyebrow")}</p>
          <h3 className="font-serif text-2xl mt-2 leading-tight">{t("admCashflowTitle")}</h3>
          <p className="text-sm text-neutral-600 mt-2 max-w-lg leading-relaxed">{t("admCashflowBody")}</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-cashflow-refresh"
          className="ed-btn ed-btn-outline disabled:opacity-50"
        >
          {loading ? "…" : t("admCashflowRefresh")}
        </button>
      </div>

      {data?.balance_error && (
        <div className="mb-4 border border-amber-300 bg-amber-50 p-3 text-xs font-mono text-amber-800 leading-relaxed" data-testid="admin-cashflow-error">
          Stripe: {data.balance_error} {t("admCashflowErrorHint")}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Stripe balance */}
        <div className="border border-neutral-200 bg-neutral-50 p-5" data-testid="admin-cashflow-balance">
          <div className="flex items-center gap-2">
            <Wallet size={12} strokeWidth={1.75} className="text-[#635BFF]" />
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600">{t("admCashflowStripeBalance")}</span>
          </div>
          <p className="font-serif text-4xl tabular-nums mt-2 text-neutral-900" data-testid="admin-cashflow-balance-value">
            {eur(data?.balance_available_cents)}
          </p>
          {data?.balance_pending_cents > 0 && (
            <p className="font-mono text-[0.6rem] text-neutral-500 mt-1">
              + {eur(data.balance_pending_cents)} {t("admCashflowPendingClearance")}
            </p>
          )}
        </div>

        {/* Pending commission */}
        <div className="border border-neutral-200 bg-neutral-50 p-5" data-testid="admin-cashflow-pending">
          <div className="flex items-center gap-2">
            <TrendingDown size={12} strokeWidth={1.75} className="text-neutral-500" />
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600">{t("admCashflowPending")}</span>
          </div>
          <p className="font-serif text-4xl tabular-nums mt-2 text-neutral-900" data-testid="admin-cashflow-pending-value">
            {eur(data?.pending_commission_cents)}
          </p>
          <p className="font-mono text-[0.6rem] text-neutral-500 mt-1">
            {data?.pending_conversions ?? 0} {t("admCashflowConversions")}
          </p>
        </div>

        {/* Gap + health signal */}
        <div className={`border p-5 ${H.bg}`} data-testid="admin-cashflow-gap">
          <div className="flex items-center gap-2">
            <H.Icon size={12} strokeWidth={1.75} className={H.text} />
            <span className={`font-mono text-[0.6rem] uppercase tracking-[0.15em] ${H.text}`}>{H.label}</span>
          </div>
          <p className={`font-serif text-4xl tabular-nums mt-2 ${H.text}`} data-testid="admin-cashflow-gap-value">
            {data && data.gap_cents >= 0 ? "+" : ""}{eur(data?.gap_cents)}
          </p>
          <p className="font-mono text-[0.6rem] text-neutral-600 mt-1">
            {t(data?.gap_cents >= 0 ? "admCashflowGapPositive" : "admCashflowGapNegative")}
          </p>
        </div>
      </div>
    </div>
  );
}
